import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { skillService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';

const SkillsContext = createContext(null);

export const useSkills = () => {
  const context = useContext(SkillsContext);
  if (!context) {
    throw new Error('useSkills must be used within a SkillsProvider');
  }
  return context;
};

export const SkillsProvider = ({ children }) => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { selectedTeam } = useTeam();
  const [initialized, setInitialized] = useState(false);
  
  // Flag pour éviter les appels multiples
  const isLoadingRef = useRef(false);

  // Charger toutes les compétences
  const loadSkills = useCallback(async (forceReload = false, teamOverride = null) => {
    // Si déjà en cours de chargement, ne pas relancer
    if (isLoadingRef.current && !forceReload) {
      console.log('⏳ SkillsContext - Chargement déjà en cours, abandon');
      return;
    }

    // Si pas d'utilisateur connecté, nettoyer
    if (!currentUser?.id) {
      console.log('👤 SkillsContext - Utilisateur non connecté:', {
        userId: currentUser?.id,
        userEmail: currentUser?.email
      });
      setSkills([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);
      
      const teamToUse = teamOverride || selectedTeam;
      console.log('🎯 SkillsContext - Chargement des compétences pour:', currentUser?.email, 'équipe:', teamToUse?.name);

      const data = await skillService.getAll(teamToUse?.id);
      setSkills(data);
      setInitialized(true);
    } catch (err) {
      console.error('❌ SkillsContext - Erreur:', err);
      setError(err.message);
      setSkills([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [currentUser?.id, currentUser?.email, selectedTeam?.id]);

  // Nettoyer les données quand l'utilisateur se déconnecte
  useEffect(() => {
    if (!currentUser?.id && initialized) {
      console.log('🧹 SkillsContext - Nettoyage des données utilisateur');
      setSkills([]);
      setError(null);
      setInitialized(false);
      setLoading(false);
    }
  }, [currentUser?.id, initialized]);

  // Écouter les changements d'équipe
  useEffect(() => {
    const handleTeamChange = (event) => {
      const { team, userId } = event.detail;
      
      if (userId === currentUser?.id) {
        console.log('🔄 SkillsContext - Changement d\'équipe détecté:', team.name);
        loadSkills(true, team); // Force le rechargement avec la nouvelle équipe
      }
    };

    window.addEventListener('teamChanged', handleTeamChange);
    
    return () => {
      window.removeEventListener('teamChanged', handleTeamChange);
    };
  }, [currentUser?.id, loadSkills]);

  // Ajouter une compétence
  const addSkill = useCallback(async (skillData) => {
    try {
      setError(null);
      console.log('➕ Ajout compétence:', skillData);
      console.log('🎯 Équipe sélectionnée pour création compétence:', selectedTeam);
      
      if (!selectedTeam || !selectedTeam.id) {
        throw new Error('Aucune équipe sélectionnée pour créer la compétence');
      }
      
      const skillDataWithTeam = {
        ...skillData,
        teamId: selectedTeam.id
      };
      
      const newSkill = await skillService.create(skillDataWithTeam);
      console.log('✅ Compétence créée:', newSkill);
      
      // Recharger toutes les compétences pour obtenir les données complètes
      // car l'API peut retourner un objet incomplet
      await loadSkills(true);
      
      return newSkill;
    } catch (err) {
      console.error('❌ Erreur lors de l\'ajout de la compétence:', err);
      setError(err.message || 'Erreur lors de l\'ajout de la compétence');
      throw err;
    }
  }, [selectedTeam, loadSkills]);

  // Mettre à jour une compétence
  const updateSkill = useCallback(async (id, skillData) => {
    try {
      setError(null);
      console.log('✏️ Mise à jour compétence:', id, skillData);
      
      if (!selectedTeam || !selectedTeam.id) {
        throw new Error('Aucune équipe sélectionnée pour modifier la compétence');
      }
      
      const skillDataWithTeam = {
        ...skillData,
        teamId: selectedTeam.id
      };
      
      const updatedSkill = await skillService.update(id, skillDataWithTeam);
      console.log('✅ Compétence mise à jour:', updatedSkill);
      
      // Recharger toutes les compétences pour obtenir les données complètes
      await loadSkills(true);
      
      return updatedSkill;
    } catch (err) {
      console.error('❌ Erreur lors de la mise à jour de la compétence:', err);
      setError(err.message || 'Erreur lors de la mise à jour de la compétence');
      throw err;
    }
  }, [selectedTeam, loadSkills]);

  // Supprimer une compétence
  const deleteSkill = useCallback(async (id) => {
    try {
      setError(null);
      console.log('🗑️ Suppression compétence:', id);
      
      await skillService.delete(id);
      console.log('✅ Compétence supprimée:', id);
      
      // Recharger toutes les compétences pour rafraîchir la liste
      await loadSkills(true);
    } catch (err) {
      console.error('❌ Erreur lors de la suppression de la compétence:', err);
      setError(err.message || 'Erreur lors de la suppression de la compétence');
      throw err;
    }
  }, [loadSkills]);

  // Récupérer une compétence par ID
  const getSkillById = useCallback((id) => {
    return skills.find(skill => skill.id === parseInt(id));
  }, [skills]);

  // Fonction d'initialisation manuelle
  const initializeData = useCallback(async (userOverride = null) => {
    const userToUse = userOverride || currentUser;
    
    if (!userToUse?.id) {
      console.log('⚠️ SkillsContext - Initialisation sans utilisateur connecté');
      return;
    }
    
    console.log('🔄 SkillsContext - Initialisation manuelle pour', userToUse.email);
    setInitialized(false);
    setLoading(true);
    setError(null);
    
    try {
      await loadSkills(true);
      setInitialized(true);
      console.log('✅ SkillsContext - Initialisation terminée');
    } catch (error) {
      console.error('❌ SkillsContext - Erreur lors de l\'initialisation:', error);
      setError(error.message || 'Erreur lors de l\'initialisation');
      setSkills([]);
    } finally {
      setLoading(false);
    }
  }, [loadSkills, currentUser]);

  const value = {
    // État
    skills,
    loading,
    error,
    initialized,
    
    // Actions
    loadSkills,
    addSkill,
    updateSkill,
    deleteSkill,
    
    // Utilitaires
    getSkillById,
    
    // Stats
    totalSkills: skills.length,
    
    // Initialisation
    initializeData,
  };

  return (
    <SkillsContext.Provider value={value}>
      {children}
    </SkillsContext.Provider>
  );
}; 